/**
 * Last-activity timestamp storage.
 *
 * Persists ONLY a numeric `Date.now()` value under
 * `mochia.lastActive.v1` so the cold-load auth path can enforce a 7-day
 * absolute idle limit (`extend-session-lifetime`, Decisions 1 & 2). The
 * persisted value is plain text (e.g. `"1700000000000"`) — never an object,
 * never the access token, never the email.
 *
 * Sharp edges:
 *   - Independent key from `mochia.account.v1`: we deliberately do NOT
 *     extend the account hint schema (Decision 1). A second key keeps
 *     `account.ts` contracts stable and means a corrupt/missing
 *     lastActive value can't poison the email hint.
 *   - This change does NOT extend the `harden-token-storage` change
 *     (Decision 10). Token storage and lastActive remain orthogonal:
 *     token in memory + opaque mirror; lastActive in localStorage as
 *     plain numeric text.
 *   - `mochia.lastActive.v1` is per-origin per-device — multi-device
 *     timing is intentionally independent and needs no server
 *     coordination (Decision 6).
 *   - Failure modes (private mode / quota): we swallow exceptions so
 *     the auth path degrades to "no-7d-gate" rather than crashing the
 *     bootstrap. Worst case is the same UX we had before this change.
 */

const KEY = 'mochia.lastActive.v1';

/**
 * Absolute session idle limit. Re-exported by `coldLoad.ts` as the default
 * `sessionMaxIdleMs` so test code can override the threshold without monkey-
 * patching this constant.
 */
export const SESSION_MAX_IDLE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Returns the persisted timestamp as a number, or null if missing /
 * malformed. Non-finite values (NaN, Infinity) are rejected — callers MUST
 * be able to subtract this from `Date.now()` without UB.
 */
export function getLastActive(): number | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null || raw.length === 0) return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return n;
  } catch {
    return null;
  }
}

/**
 * Persist a timestamp to `mochia.lastActive.v1`. Defaults to `Date.now()`.
 *
 * Test-friendly: callers may pass an explicit timestamp; production code
 * always omits the argument.
 */
export function setLastActive(ts: number = Date.now()): void {
  if (typeof localStorage === 'undefined') return;
  if (!Number.isFinite(ts)) return;
  try {
    localStorage.setItem(KEY, String(ts));
  } catch {
    /* private mode / quota — non-fatal, see file header */
  }
}

/** Remove the key entirely. Called on logout and on the 7-day idle gate. */
export function clearLastActive(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* non-fatal */
  }
}
